import type { CSSProperties } from 'react';
import { Asterisk, Check, Plug } from 'lucide-react';
import { BrandLogo } from '@/components/ui/brand-logo';
import l from '@/components/marketing/landing/landing.module.css';
import s from '@/components/marketing/page/page.module.css';
import type { IntegrationEntry } from '@/lib/brand/integrations-catalog';
import { cn } from '@/lib/utils';
import { statusCopy } from './status';

const logoKeys = new Set([
  'shopify',
  'ebay',
  'stripe',
  'paypal',
  'etsy',
  'xero',
  'square',
  'venmo',
  'quickbooks',
  'zapier',
  'amazon',
  'plaid',
  'veeqo',
  'shipstation',
]);

export function IntegrationLogo({
  integration,
  size = 26,
}: {
  integration: IntegrationEntry;
  size?: number;
}) {
  return logoKeys.has(integration.logoKey) ? (
    <BrandLogo
      name={integration.logoKey}
      alt=""
      size={integration.logoKey === 'ebay' ? size + 10 : size}
    />
  ) : (
    <Plug className="size-5 text-gray-500" aria-hidden="true" />
  );
}

/** Self-hosted wordmarks, drawn as one-color silhouettes. Ratios match the
    home page marquee. */
const maskedWordmarks: Record<string, { ratio: number; cropTop?: boolean }> = {
  plaid: { ratio: 126 / 48 },
  amazon: { ratio: 603 / 182 },
  shipstation: { ratio: 280 / 44 },
  veeqo: { ratio: 253 / 60, cropTop: true },
};

/** One-tone logo for dark surfaces, sized to sit in a square tile. */
export function MonoLogo({
  integration,
  width = 72,
}: {
  integration: IntegrationEntry;
  width?: number;
}) {
  const wordmark = maskedWordmarks[integration.logoKey];
  if (wordmark) {
    const w = Math.min(width, Math.round(26 * wordmark.ratio));
    return (
      <span
        className={l.maskLogo}
        style={
          {
            '--logo': `url(/logos/integrations/${integration.logoKey}.svg)`,
            '--logo-size': wordmark.cropTop ? '100% auto' : 'contain',
            width: w,
            height: Math.round(w / wordmark.ratio),
          } as CSSProperties
        }
        aria-hidden="true"
      />
    );
  }
  // Matches the home page marquee: Zapier's mark does not hold up small, and
  // eBay's glyph is a wordmark inside a square viewBox.
  if (integration.logoKey === 'zapier') {
    return <Asterisk className="size-6" strokeWidth={3} aria-hidden="true" />;
  }
  if (logoKeys.has(integration.logoKey)) {
    return (
      <BrandLogo
        name={integration.logoKey}
        alt=""
        size={integration.logoKey === 'ebay' ? 46 : 24}
      />
    );
  }
  return <Plug className="size-5" aria-hidden="true" />;
}

export function StatusPill({ status }: { status: IntegrationEntry['status'] }) {
  const copy = statusCopy[status];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset',
        copy.tone
      )}
    >
      {status === 'available' && (
        <span className="relative flex size-1.5">
          <span className="absolute inset-0 animate-ping rounded-full bg-emerald-500 opacity-60 motion-reduce:hidden" />
          <span className="relative size-1.5 rounded-full bg-emerald-500" />
        </span>
      )}
      {copy.label}
    </span>
  );
}

interface IntegrationCardProps {
  integration: IntegrationEntry;
  index?: number;
  /** Animate in on render, for filtered lists. */
  animate?: boolean;
}

export function IntegrationCard({
  integration,
  index = 0,
  animate = false,
}: IntegrationCardProps) {
  const planned = integration.status === 'planned';
  return (
    <article
      className={cn(s.card, animate && s.fadeIn, 'flex w-full flex-col p-6')}
      style={{ '--i': index } as CSSProperties}
      data-spotlight
    >
      <div className="flex items-start justify-between gap-3">
        <span
          className={cn(
            'flex size-12 items-center justify-center rounded-2xl bg-white shadow-[0_0_0_1px_rgb(15_23_42/0.08),0_4px_12px_-6px_rgb(15_23_42/0.2)]',
            planned && 'opacity-60 grayscale'
          )}
        >
          <IntegrationLogo integration={integration} />
        </span>
        <StatusPill status={integration.status} />
      </div>
      <h3 className="mt-5 text-lg font-semibold tracking-tight text-gray-950">
        {integration.name}
      </h3>
      <p className="mt-1.5 text-[15px] leading-relaxed text-gray-600">
        {integration.description}
      </p>
      {integration.features.length > 0 && (
        <ul className="mt-5 space-y-2 border-t border-gray-950/[0.06] pt-5">
          {integration.features.slice(0, 4).map(feature => (
            <li key={feature} className="flex gap-2.5 text-sm text-gray-700">
              <Check
                className={cn(
                  'mt-0.5 size-4 shrink-0',
                  planned ? 'text-gray-300' : 'text-amber-600'
                )}
                aria-hidden="true"
              />
              {feature}
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}
